import AbstractComparison from 'components/search/AbstractComparison';
import { deepMerge } from 'toolbox/deepMerge';
import { on, off, trigger } from 'toolbox/event';
import { Event } from 'services/EventEmitter';
import { mix } from 'core/mixwith';
import Accessibility from 'mixins/Accessibility';
import AnalyticsMixin from 'mixins/Analytics';

/**
 * This is a description of the ComparisonPreviewSection constructor function.
 * @class
 * @classdesc ComparisonPreviewSection class designed to handle client-side logic
 * related to comparison preview section functionality.
 * @extends AbstractComparison
 */
export default class ComparisonPreviewSection extends mix(AbstractComparison).with(Accessibility, AnalyticsMixin) {
    /**
     * Constructor of the class that mainly merge the options of the components
     * @param {HTMLElement} element HTMLElement of the component
     * @param {object} options options that belongs to the component
     */
    constructor(element, options = {}) {
        super(element, deepMerge({
            isValid: false,
            comparisonModal: null, // comparison modal options
            footerPrefix: 'footer', // used to hide/show footer sticky bar
        }, options));
    }

    /**
     * All selectors must be cached. Never cache elements that are out of the component scope
     */
    initCache() {
        this.selectors.products = this.element.querySelectorAll('[data-js-selected-product]');
        this.selectors.compareButton = this.element.querySelector('[data-js-compare]');

        this.selectors.previewItemRemove = '[data-js-selected-remove]';

        this.classes = {
            expanded: 'm-expanded',
        };
    }

    /**
     * Should contain only event listeners and nothing else
     * All the event handlers should be into a separated function. No usage of anonyous function
     */
    bindEvents() {
        on('click', this.selectors.products, this.onProductClick.bind(this));
        on('click', this.selectors.compareButton, this.onCompareButtonClick.bind(this));
    }

    /**
     * Selected product item "click" event handler
     * @param {Object} event event object
     */
    onProductClick(event) {
        if (event.target.matches('[data-js-selected-remove]')) {
            // remove product from list of selected products
            const productElement = event.target.closest('[data-js-selected-product]');
            const pid = productElement && productElement.getAttribute('data-js-pid');

            // save index of the removed item to restore focus on the nearest remove button later
            this.setRemovedIndex(event.target, Array.from(this.element.querySelectorAll(this.selectors.previewItemRemove)));

            this.removeProduct(pid);
        }
    }

    /**
     * Compare button "click" event handler
     */
    onCompareButtonClick() {
        if (this.options.isValid) {
            Event.emit('modal.open', this.options.comparisonModal);

            this.analytics.label = '{{dataLayer.page.category}}';
            this.sendAnalytics(this.analytics);
        }
    }

    /**
     * Returns object with product ids
     * @param {Array} products array of selected pids
     * @returns {Object} object with selected pid
     */
    getReloadData(products) {
        const params = super.getReloadData(products);

        params.expanded = this.element.classList.contains(this.classes.expanded)
            // if first product selected, expand section
            || (Object.keys(this.options.selectedProducts).length === 0 && Object.keys(products).length === 1);

        return params;
    }

    /**
     * After init
     * Run any script after the component is fully initialized
     */
    afterInit() {
        this.toggleStickyBars(this.options.selectedProducts.length > 0);
        super.afterInit();
        // restore focus on remove or toggle button
        this.restoreFocusAfterRemove(Array.from(this.element.querySelectorAll(this.selectors.previewItemRemove)));

        if (this.options.comparisonModal) {
            this.options.comparisonModal.options.contentUrl += `&comparisonId=${this.state.instanceId}`;
        }
    }

    /**
    * Show and hide preview section and footer sticky bar
    *  @param {boolean} stickyBarState boolean value to toggle stickybar
    */
    toggleStickyBars(stickyBarState) {
        let event;
        if (stickyBarState) {
            event = 'stickybar.show';
            Event.emit(`${this.options.footerPrefix}.stickybar.hide`);
        } else {
            event = 'stickybar.hide';
            Event.emit(`${this.options.footerPrefix}.stickybar.show`);
        }
        trigger(event, this.element, { bubbles: true });
    }

    /**
    * Store update hook
    * @param {Object} state new state
    */
    storeUpdated(state) {
        const currentState = state[this.state.instanceId];
        this.toggleStickyBars(currentState.showComparisonButtons);
        this.syncProducts(state);
    }

    /**
     * Destroy is called automatically after the component is being removed from the DOM
     * You must always destroy the listeners attached to an element to avoid any memory leaks
     */
    destroy() {
        off('click', this.selectors.products);
        off('click', this.selectors.compareButton);
    }
}
